/* eslint-disable react/prop-types */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import Button2 from 'components/buttons/Button2/Button2';
import { LocalizedMessage } from 'intl/LocalizedComponents';

import { ReactComponent as InfoIcon } from './icons/info.svg';
import { ReactComponent as CloseIcon } from './icons/close.svg';

import styles from './styles.cssmodule.css';

function Modal2({
  modalType, // info | warning | alert | success
  size, // large | medium
  position, // center | top
  loading,

  isOpen,
  onClose,

  title,
  icon,
  subtitle,

  children,

  actions,
}) {
  const dialogRef = useRef(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    isOpen ? dialog.showModal() : dialog.close();
  }, [isOpen]);

  const handleClick = (e) => {
    const dialogDimensions = dialogRef.current.getBoundingClientRect();
    if (dialogDimensions) {
      const clickedInDialog = (
        e.clientX >= dialogDimensions.left
        && e.clientX <= dialogDimensions.right
        && e.clientY >= dialogDimensions.top
        && e.clientY <= dialogDimensions.bottom
      );
      if (!clickedInDialog && !loading) {
        onClose();
      }
    }
  };

  const TopBorder = () => <div className={styles.topBorder} />;

  const CloseButton = () => (
    <button
      type="button"
      onClick={onClose}
      className={styles.closeButton}
      aria-label="Close modal"
    >
      <CloseIcon />
    </button>
  );

  const Actions = actions && actions.map((action) => (
    <Button2
      type={action.type || modalType}
      key={action.key || action.label}
      loading={loading && action.showLoading}
      disabled={loading || action.disabled}
      onClick={action.onClick}
    >
      <LocalizedMessage id={action.label} />
    </Button2>
  ));

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <dialog
      className={styles.dialog}
      data-type={modalType}
      data-position={position}
      data-size={size}
      ref={dialogRef}
      onClick={handleClick}
      onClose={onClose}
    >
      <TopBorder />
      <CloseButton />

      <ModalContent>

        <ModalIcon>
          { icon || <InfoIcon /> }
        </ModalIcon>

        <ModalTitle>
          { title }
        </ModalTitle>

        <ModalSubtitle>
          { subtitle }
        </ModalSubtitle>

        <ModalBody>
          {children}
        </ModalBody>

      </ModalContent>

      {actions && <ModalDivider />}

      <ModalActions>
        {Actions}
      </ModalActions>
    </dialog>
  );
}

/* =============== */
/*  Subcomponents  */
/* =============== */

/*  Title  */
/* ======= */
function ModalTitle({ children }) {
  return <h2 className={styles.title}>{children}</h2>;
}
ModalTitle.propTypes = {
  children: PropTypes.node.isRequired,
};

/*  Subtitle  */
/* ========== */
function ModalSubtitle({ children }) {
  return children && <p className={styles.subtitle}>{children}</p>;
}
ModalSubtitle.propTypes = {
  children: PropTypes.node,
};
ModalSubtitle.defaultProps = {
  children: null,
};

/*  Icon  */
/* ====== */

function ModalIcon({ children: Icon }) {
  return (
    <div className={styles.iconContainer}>
      {Icon}
    </div>
  );
}

ModalIcon.propTypes = {
  children: PropTypes.element.isRequired,
};

/*  Content  */
/* ========= */

function ModalContent({ children }) {
  return <div className={styles.content}>{children}</div>;
}
ModalContent.propTypes = {
  children: PropTypes.node.isRequired,
};

/*  Body  */
/* ====== */
function ModalBody({ children }) {
  return <div className={styles.body}>{children}</div>;
}
ModalBody.propTypes = {
  children: PropTypes.node.isRequired,
};

/*  Divider  */
/* ========= */

function ModalDivider() {
  return <div className={styles.divider} />;
}

/*  Actions  */
/* ========= */
function ModalActions({ children }) {
  return children && (
    <div className={styles.actions}>
      {children}
    </div>
  );
}
ModalActions.propTypes = {
  children: PropTypes.node.isRequired,
};

Modal2.propTypes = {
  modalType: PropTypes.string,
  size: PropTypes.string,
  position: PropTypes.oneOf(['center', 'top']),
  isOpen: PropTypes.bool,
  loading: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  icon: PropTypes.node,
  subtitle: PropTypes.string,
  children: PropTypes.node,
  actions: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    label: PropTypes.string,
    disabled: PropTypes.bool,
    type: PropTypes.oneOf(['primary', 'primaryOutline', 'secondaryOutline', 'info', 'success', 'warning', 'alert']),
    onClick: PropTypes.func,
  })),
};

Modal2.defaultProps = {
  modalType: 'info',
  size: 'medium',
  position: 'center',
  loading: false,
  isOpen: false,
  onClose: null,
  title: 'Modal',
  icon: null,
  subtitle: null,
  children: null,
  actions: null,
};

export default Modal2;



// WEBPACK FOOTER //
// ./components/modals/Modal2/Modal2.js